<script setup lang="ts">
import type { ISatOtpMethodSelectorProps } from "@chatfood/design-system";
import { useToast, SatOtpMethodSelector } from "@chatfood/design-system";
import { ref, computed, onBeforeMount } from "vue";
import { bemBuilder, phoneBeautifier } from "@chatfood/core-utils";
import { AuthRepoError } from "@chatfood/dbe-client/errors";
import type { IOtpDeliverySelectorProps } from "./props";
import type { IOtpDeliverySelectorEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { useLocale } from "~/composables/use-locale";
import { useBugReporter, useDirectBackend, useFeatureFlag } from "#imports";

const props = defineProps<IOtpDeliverySelectorProps>();
const emits = defineEmits<IOtpDeliverySelectorEmits>();

const { t } = useLocale();
const { report } = useBugReporter();
const { toastError } = useToast();
const { authRepo } = useDirectBackend();
const { isEnabled } = useFeatureFlag();

const css = bemBuilder("otp-delivery-selector");

const business = useBusinessStore();

const displayPhoneNumber = computed<string>(() => {
  if (!props.countryCode) {
    return props.phoneNumber;
  }

  const fullPhoneNumber = props.countryCode + props.phoneNumber;

  return (
    phoneBeautifier(props.phoneNumber, props.countryCode) ?? fullPhoneNumber
  );
});

const isRequestingOtp = ref(false);

const onSendOtp = async (
  channel?: ISatOtpMethodSelectorProps["methods"][0],
) => {
  if (!channel) {
    toastError(
      t("components.otp_delivery_selector.otp_selector_validation_error"),
    );
    return;
  }

  isRequestingOtp.value = true;

  try {
    const isEmailMethod = channel.key === "email";
    const isSmsMethod = channel.key === "sms";

    let payload;

    if (isEmailMethod || !props.countryCode) {
      payload = {
        messagingChannel: isEmailMethod ? ("email" as const) : channel.key,
        key: "email" as const,
        email: props.email,
      };
    } else {
      payload = {
        messagingChannel: isSmsMethod
          ? ("sms" as const)
          : ("whatsapp" as const),
        key: "phone" as const,
        phoneNumber: {
          countryCode: props.countryCode!,
          number: props.phoneNumber,
        },
      };
    }

    await authRepo.requestOtp({
      businessId: business.id,
      ...payload,
    });

    emits("success", channel.key);
  } catch (err: unknown) {
    if (err instanceof AuthRepoError && err.code === "tooManyRequests") {
      toastError(
        t("components.otp_delivery_selector.error_too_many_otp_requests", {
          seconds: props.throttleSeconds,
        }),
      );

      return;
    }

    toastError(t("components.otp_delivery_selector.request_otp_failure"));
    report(err);

    emits("failure");
  } finally {
    isRequestingOtp.value = false;
  }
};

const hideOtpWhatsapp = ref(false);

onBeforeMount(async () => {
  if (await isEnabled("direct.hideOtpWhatsapp")) {
    hideOtpWhatsapp.value = true;
  }
});

const methods = computed<ISatOtpMethodSelectorProps["methods"]>(() => {
  const availableMethods = [
    {
      key: "email",
      text: t("components.otp_delivery_selector.channel_otp_email"),
      subText: props.email,
    },
    {
      key: "sms",
      text: t("components.otp_delivery_selector.channel_otp_sms"),
      subText: displayPhoneNumber.value,
    },
  ];

  if (!hideOtpWhatsapp.value) {
    availableMethods.push({
      key: "whatsapp",
      text: t("components.otp_delivery_selector.channel_otp_whatsapp"),
      subText: displayPhoneNumber.value,
    });
  }

  return availableMethods;
});
</script>

<template>
  <SatOtpMethodSelector
    :class="css('')"
    :is-loading="isRequestingOtp"
    :cta="t('components.otp_delivery_selector.otp_selector_cta')"
    :title="t('components.otp_delivery_selector.otp_selector_title')"
    :headline="t('components.otp_delivery_selector.otp_selector_subtext')"
    :method-title="
      t('components.otp_delivery_selector.otp_selector_methods_title')
    "
    :methods="methods"
    :default-value="methods[0]"
    @select="onSendOtp"
  />
</template>
